import { Tooltip } from '@mui/material';
import clsx from 'clsx';
import { Mic, SendHorizontal, TriangleAlert, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { useAppSelector } from 'components/app/redux/store';

import { MessageRecord } from '../../types/message';
import { Activator } from '../activator';
import { IconButton } from '../icon-button';
import { Message } from '../message';
import { TextInput } from '../text-input';
import { VoiceInput } from '../voice-input';

import styles from './styles.module.css';

type Props = {
  readonly loading: boolean;
  readonly history: Array<MessageRecord>;
  readonly onTextRequest: (question: string) => void;
  readonly onVoiceRequest: (audio: Blob) => void;
  readonly onErrorReportRequest: () => void;
  readonly responseText: string;
};

export const Dialog: React.FC<Props> = props => {
  const { history } = props;

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [textInput, setTextInput] = useState<string>('');
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const {
    inputPlaceholder,
    errorReportIcon,
    chatTitle,
    chatTitleSecond,
    voicePlaceholder,
  } = useAppSelector(state => state.localization.components.ChatBot.messages);

  useEffect(() => {
    if (props.responseText) {
      setTextInput(props.responseText);
    }
  }, [props.responseText]);

  const handleTextRequest = () => {
    props.onTextRequest(textInput);
    setTextInput('');
  };

  const handleErrorReportRequest = () => {
    props.onErrorReportRequest();
  };

  const handleVoiceRequest = (blob: Blob) => {
    props.onVoiceRequest(blob);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      textInput.length && handleTextRequest();
    }
  };

  return (
    <div className={styles.wrapper}>
      {!isOpen && <Activator onClick={() => setIsOpen(true)} />}
      {isOpen && (
        <div className={clsx(styles.container)}>
          <div className={styles.headline}>
            <div className={styles.title}>
              <div>{chatTitle}</div>
              <span>{chatTitleSecond}</span>
            </div>
            <div className={styles.icons}>
              <Tooltip title={errorReportIcon}>
                <div onClick={handleErrorReportRequest}>
                  <TriangleAlert size={20} />
                </div>
              </Tooltip>
              <div onClick={() => setIsOpen(false)}>
                <X size={20} />
              </div>
            </div>
          </div>
          <div className={styles.messagesContainer}>
            {history.map((record, index) => (
              <Message key={index} {...record} />
            ))}
          </div>
          <div className={styles.actions}>
            <TextInput
              placeholder={isRecording ? voicePlaceholder : inputPlaceholder}
              value={textInput}
              onChange={setTextInput}
              onKeyDown={handleKeyDown}
            />
            <div className={styles.actionButtons}>
              <VoiceInput
                maxDuration={10}
                onRecordingComplete={handleVoiceRequest}
                onRecordingStateChange={setIsRecording}
              />
              {!isRecording && (
                <IconButton
                  loading={props.loading}
                  disabled={!textInput.length}
                  onClick={handleTextRequest}
                >
                  <SendHorizontal />
                </IconButton>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
